import React, { useState, useEffect, useRef } from 'react';
import classnames from 'classnames';
import Dropzone, { FileRejection } from 'react-dropzone';
import { useTranslation } from 'react-i18next';

import { useFileUpload } from '../../_hocs/withFileUpload';
import { useVerification } from '../../../VerificationPopup/withVerification';
import { MIME_CONFIG, mimeTypesToDropzoneAccept } from '../../../utils/mime';
import s from './UploadDropzone.scss';

export interface Props {
  className?: string;
}

const UploadDropzone: React.FC<Props> = ({ className }) => {
  const { t } = useTranslation();
  const { uploadFiles } = useFileUpload();
  const { isVerificationNeeded, pushVerificationPopup } = useVerification();
  const [dragInProgress, setDragInProgress] = useState(false);
  const dragCountRef = useRef(0);

  useEffect(() => {
    const onDragEnterLeave = (e: DragEvent) => {
      e.preventDefault();
      switch (e.type) {
        case 'dragenter':
          dragCountRef.current++;
          break;
        case 'dragleave':
          dragCountRef.current--;
          break;
        case 'drop':
          dragCountRef.current = 0;
          break;
        default:
        // noop
      }
      setDragInProgress(dragCountRef.current === 1);
    };

    document.addEventListener('dragenter', onDragEnterLeave, true);
    document.addEventListener('dragleave', onDragEnterLeave, true);
    document.addEventListener('drop', onDragEnterLeave, true);

    return () => {
      document.removeEventListener('dragenter', onDragEnterLeave, true);
      document.removeEventListener('dragleave', onDragEnterLeave, true);
      document.removeEventListener('drop', onDragEnterLeave, true);
    };
  }, []);

  const onFileDrop = (
    acceptedFiles: File[],
    fileRejections: FileRejection[]
  ) => {
    if (acceptedFiles.length === 0) {
      return;
    }

    if (isVerificationNeeded) {
      pushVerificationPopup('submit');
      return;
    }

    uploadFiles(acceptedFiles.map(file => ({ file })));
  };

  return (
    <div className={classnames(s['root'], dragInProgress && s['root-active'])}>
      <Dropzone
        noClick={true}
        multiple={true}
        accept={mimeTypesToDropzoneAccept(MIME_CONFIG.forMediaManager)}
        onDrop={onFileDrop}
      >
        {({
          isDragActive,
          isDragAccept,
          isDragReject,
          getRootProps,
          getInputProps,
        }) => (
          <div
            {...getRootProps({
              className: classnames(
                s['dropzone'],
                className,
                isDragActive && s['dropzone-active'],
                isDragAccept && s['dropzone-accept'],
                isDragReject && s['dropzone-reject']
              ),
            })}
          >
            <input {...getInputProps()} />
            {isDragReject && t('mediapopup.upload.dropzone.reject')}
            {isDragActive &&
              !isDragReject &&
              t('mediapopup.upload.dropzone.accept')}
          </div>
        )}
      </Dropzone>
    </div>
  );
};

export default UploadDropzone;
